import { useCallback } from "react"
import { useLocation, useNavigate } from "react-router"

import { usePermissions } from "@/admin/hooks/use-permissions"
import { useConfigReturn } from "./config-return-context"

/**
 * Config help is gated to internal staff and org admins. `can()` already
 * short-circuits to true for staff/superuser and org is_admin roles, so this
 * returns true for them automatically; the codename lets curated roles be
 * granted the affordance later.
 */
export function useConfigHelpEnabled(): boolean {
  const { can } = usePermissions()
  return can("config-help.view")
}

/**
 * Returns a handler that stashes the current surface as the return target and
 * navigates to a dedicated config page. Used by both the inline "Edit config"
 * link and the tip links inside the "?" popover.
 */
export function useOpenConfig() {
  const navigate = useNavigate()
  const location = useLocation()
  const { setConfigReturn } = useConfigReturn()

  return useCallback(
    (route: string, returnLabel: string) => {
      // Record the destination pathname (drop any hash/query) so the return
      // banner can dismiss itself once the user navigates off that page.
      const destination = route.split(/[?#]/)[0]
      setConfigReturn({
        label: returnLabel,
        path: `${location.pathname}${location.search}`,
        destination,
      })
      navigate(route)
    },
    [navigate, location.pathname, location.search, setConfigReturn],
  )
}
